import React, { useState } from 'react';
import styled from 'styled-components';
import { FaUser, FaEnvelope, FaTrashAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import Icon from '../Icon/Icon';
import AnimatedCard from '../AnimatedCard/AnimatedCard';

// EmailJS Configuration for Account Deletion
const EMAILJS_CONFIG = {
  PUBLIC_KEY: '4i8G7EykATzzz6Tzb',
  SERVICE_ID: 'service_4h3nirm',
  DELETION_TEMPLATE_ID: 'template_deletion', // Single template with auto-reply enabled
};

const DeletionSection = styled.div`
  width: 100%;
  max-width: 800px;
`;

const Container = styled.div`
  width: 100%;
  position: relative;
  z-index: 10;
`;

const Title = styled.h2`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: clamp(2rem, 5vw, 2.5rem);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-size: 1.8rem;
  }
`;

const FormTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  text-align: center;
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: 0.9rem;
  line-height: 1.6;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: 0.9rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.primary};
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xl}`};
  border: 2px solid ${({ theme }) => `${theme.colors.primary}33`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }

  &::placeholder {
    color: ${({ theme }) => `${theme.colors.text}66`};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xl}`};
  border: 2px solid ${({ theme }) => `${theme.colors.primary}33`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xl}`};
  border: 2px solid ${({ theme }) => `${theme.colors.primary}33`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme }) => theme.colors.background};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }

  &::placeholder {
    color: ${({ theme }) => `${theme.colors.text}66`};
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => `${theme.colors.primary}11`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const Checkbox = styled.input`
  margin-top: 4px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.95rem;
  line-height: 1.6;
  cursor: pointer;

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitButton = styled(motion.button)`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    background-color: #c82333;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #dc354566;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-align: center;
  font-weight: 500;
  background-color: ${({ type }) => 
    type === 'success' 
      ? '#d4edda' 
      : '#f8d7da'};
  color: ${({ type }) => 
    type === 'success' 
      ? '#155724' 
      : '#721c24'};
  border: 1px solid ${({ type }) => 
    type === 'success' 
      ? '#c3e6cb' 
      : '#f5c6cb'};
`;

interface FormData {
  fullName: string;
  email: string;
  reason: string;
  additionalInfo: string;
  confirmDeletion: boolean;
}

const AccountDeletion: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    reason: '',
    additionalInfo: '',
    confirmDeletion: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Initialize EmailJS
  React.useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.confirmDeletion) {
      setStatus({
        type: 'error',
        message: 'Please confirm that you understand the consequences of account deletion.'
      });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
  
    try {
      // Generate a unique request ID for tracking
      const requestId = `DEL-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create confirmation URL (this will be handled by a separate confirmation page)
      const confirmationUrl = `${window.location.origin}/confirm-deletion?id=${requestId}&email=${encodeURIComponent(formData.email)}`;
      
      // Send deletion request email to support@radyofone.com
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.DELETION_TEMPLATE_ID,
        {
          request_id: requestId,
          from_name: formData.fullName,
          from_email: formData.email,
          deletion_reason: formData.reason,
          additional_info: formData.additionalInfo || 'No additional information provided',
          confirmation_url: confirmationUrl,
          request_date: new Date().toLocaleString(),
          to_email: 'support@radyofone.com',
          is_confirmation: ''
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );
  
      console.log('Deletion request sent to support successfully');
      
      // Send confirmation email to user (using same template, different recipient)
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.DELETION_TEMPLATE_ID,
        {
          request_id: requestId,
          from_name: formData.fullName,
          from_email: formData.email,
          deletion_reason: formData.reason,
          additional_info: formData.additionalInfo || 'No additional information provided',
          confirmation_url: confirmationUrl,
          request_date: new Date().toLocaleString(),
          to_email: formData.email, // Send to user this time
          send_to_user: 'true' // Flag to show user version
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );
      
      console.log('Confirmation email sent to user successfully');
      
      setStatus({
        type: 'success',
        message: 'Your account deletion request has been submitted successfully. You will receive a confirmation email shortly. Please check your inbox (and spam folder) and click the confirmation button to proceed with the deletion.'
      });
      
      // Reset form
      setFormData({ 
        fullName: '', 
        email: '', 
        reason: '', 
        additionalInfo: '', 
        confirmDeletion: false 
      });
    } catch (error: any) {
      console.error('Email sending failed:', error);
      
      // Check if it's a template error
      const isTemplateError = error?.status === 400 || error?.text?.includes('template');
      
      setStatus({
        type: 'error',
        message: isTemplateError 
          ? 'The email service is not fully configured yet. Please contact us directly at support@radyofone.com to request account deletion.' 
          : 'Sorry, there was an error submitting your request. Please try again or contact us directly at support@radyofone.com.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DeletionSection>
      <Container>
        <Title>Account Deletion Request</Title>

        <AnimatedCard delay={0.2}>
          <FormTitle>Deletion Request Form</FormTitle>
          <FormSubtitle>
            We're sorry to see you go. Please fill out the form below to request account deletion.
          </FormSubtitle>
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <InputLabel htmlFor="fullName">Full Name *</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Icon icon={FaUser} size={16} />
                </InputIcon>
                <Input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                />
              </InputWrapper>
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="email">Email Address *</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Icon icon={FaEnvelope} size={16} />
                </InputIcon>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your registered email address"
                  required
                />
              </InputWrapper>
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="reason">Reason for Deletion *</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Icon icon={FaTrashAlt} size={16} />
                </InputIcon>
                <Select
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a reason...</option>
                  <option value="no_longer_using">No longer using the app</option>
                  <option value="privacy_concerns">Privacy concerns</option>
                  <option value="found_alternative">Found an alternative service</option>
                  <option value="technical_issues">Technical issues</option>
                  <option value="too_many_ads">Too many advertisements</option>
                  <option value="missing_features">Missing features</option>
                  <option value="other">Other</option>
                </Select>
              </InputWrapper>
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="additionalInfo">Additional Information (Optional)</InputLabel>
              <InputWrapper>
                <InputIcon style={{ top: '12px' }}>
                  <Icon icon={FaTrashAlt} size={16} />
                </InputIcon>
                <TextArea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  placeholder="Please provide any additional details about your decision..."
                />
              </InputWrapper>
            </InputGroup>

            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="confirmDeletion"
                name="confirmDeletion"
                checked={formData.confirmDeletion}
                onChange={handleInputChange}
                required
              />
              <CheckboxLabel htmlFor="confirmDeletion">
                <strong>I understand *</strong> that this action will permanently delete my account and all associated data, and this action cannot be undone.
              </CheckboxLabel>
            </CheckboxWrapper>

            {status && (
              <StatusMessage type={status.type}>
                {status.message}
              </StatusMessage>
            )}

            <SubmitButton
              type="submit"
              disabled={isSubmitting || !formData.confirmDeletion}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon icon={FaTrashAlt} size={18} />
              {isSubmitting ? 'Submitting Request...' : 'Submit Deletion Request'}
            </SubmitButton>
          </Form>
        </AnimatedCard>
      </Container>
    </DeletionSection>
  );
};

export default AccountDeletion;

